import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Cookie, Check, ExternalLink } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'teacherHappyCookieConsent';

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!hasConsented) {
      // Small delay before showing for smoother UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4"
          data-testid="cookie-consent-banner"
        >
          <div 
            className="max-w-4xl mx-auto rounded-2xl shadow-2xl border-2 overflow-hidden"
            style={{ 
              backgroundColor: 'var(--th-card-bg)',
              borderColor: 'var(--th-card-border)'
            }}
          >
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 p-4">
              {/* Cookie Icon - Hidden on mobile for compact view */}
              <div 
                className="hidden sm:flex w-12 h-12 rounded-xl items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--th-background-alt)' }}
              >
                <Cookie 
                  className="w-6 h-6" 
                  style={{ color: 'var(--th-primary)' }} 
                />
              </div>

              {/* Text Content */}
              <div className="flex-1 text-center sm:text-left">
                <p 
                  className="text-sm sm:text-base leading-relaxed"
                  style={{ color: 'var(--th-text-secondary)' }}
                >
                  <span className="sm:hidden">🍪 </span>
                  {t('cookies.bannerText')}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <Link
                  to="/cookies"
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: 'var(--th-background-alt)',
                    color: 'var(--th-text-secondary)',
                    border: '2px solid var(--th-card-border)'
                  }}
                  data-testid="cookie-learn-more"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>{t('cookies.learnMore')}</span>
                </Link>
                
                <button
                  onClick={handleAccept}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold transition-all hover:scale-105 shadow-lg"
                  style={{ 
                    backgroundColor: 'var(--th-primary)',
                    color: '#1E293B'
                  }}
                  data-testid="cookie-accept"
                >
                  <Check className="w-4 h-4" />
                  <span>{t('cookies.accept')}</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
